import { pickConversationSearch } from '#/shared/lib/table'
import type { ConversationSearch } from '#/shared/lib/table'

import { PERFORMANCE_RANGE_KEYS } from './agent-performance'
import type { PerformanceRangeKey } from './agent-performance'
import { isIsoDate } from './date-range'
import { MONTHLY_TARGETS_RANGE_KEYS } from './monthly-targets'
import type { MonthlyTargetsRangeKey } from './monthly-targets'
import { PIPELINE_RANGE_KEYS } from './pipeline'
import type { PipelineRangeKey } from './pipeline'
import { RECENT_ACTIVITY_SEGMENT_KEYS } from './recent-activity'
import type { RecentActivitySegmentKey } from './recent-activity'

export interface DashboardSearch extends ConversationSearch {
  pipelineRange?: PipelineRangeKey
  activityTab?: RecentActivitySegmentKey
  performanceRange?: PerformanceRangeKey
  performanceFrom?: string
  performanceTo?: string
  targetsRange?: MonthlyTargetsRangeKey
  targetsMonth?: number
  teamRange?: PerformanceRangeKey
  teamFrom?: string
  teamTo?: string
}

const PIPELINE_RANGE_VALUES = new Set<string>(
  Object.values(PIPELINE_RANGE_KEYS),
)
const ACTIVITY_TAB_VALUES = new Set<string>(
  Object.values(RECENT_ACTIVITY_SEGMENT_KEYS),
)
const PERFORMANCE_RANGE_VALUES = new Set<string>(
  Object.values(PERFORMANCE_RANGE_KEYS),
)
const TARGETS_RANGE_VALUES = new Set<string>(
  Object.values(MONTHLY_TARGETS_RANGE_KEYS),
)

const TARGETS_MONTH_MIN = -12
const TARGETS_MONTH_MAX = -2

export function validateDashboardSearch(
  raw: Record<string, unknown>,
): DashboardSearch {
  const out: DashboardSearch = {}
  pick(
    raw,
    'pipelineRange',
    PIPELINE_RANGE_VALUES,
    (v) => (out.pipelineRange = v as PipelineRangeKey),
  )
  pick(
    raw,
    'activityTab',
    ACTIVITY_TAB_VALUES,
    (v) => (out.activityTab = v as RecentActivitySegmentKey),
  )
  pick(
    raw,
    'performanceRange',
    PERFORMANCE_RANGE_VALUES,
    (v) => (out.performanceRange = v as PerformanceRangeKey),
  )
  pick(
    raw,
    'teamRange',
    PERFORMANCE_RANGE_VALUES,
    (v) => (out.teamRange = v as PerformanceRangeKey),
  )
  pickDate(raw, 'performanceFrom', (v) => (out.performanceFrom = v))
  pickDate(raw, 'performanceTo', (v) => (out.performanceTo = v))
  pickDate(raw, 'teamFrom', (v) => (out.teamFrom = v))
  pickDate(raw, 'teamTo', (v) => (out.teamTo = v))
  pick(
    raw,
    'targetsRange',
    TARGETS_RANGE_VALUES,
    (v) => (out.targetsRange = v as MonthlyTargetsRangeKey),
  )
  const month = coerceInt(raw['targetsMonth'])
  if (
    month != null &&
    month >= TARGETS_MONTH_MIN &&
    month <= TARGETS_MONTH_MAX
  ) {
    out.targetsMonth = month
  }
  return { ...out, ...pickConversationSearch(raw) }
}

function pick(
  raw: Record<string, unknown>,
  key: keyof DashboardSearch,
  allowed: Set<string>,
  assign: (value: string) => void,
): void {
  const value = raw[key]
  if (typeof value === 'string' && allowed.has(value)) assign(value)
}

function pickDate(
  raw: Record<string, unknown>,
  key: keyof DashboardSearch,
  assign: (value: string) => void,
): void {
  const value = raw[key]
  if (isIsoDate(value)) assign(value)
}

function coerceInt(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw)
  if (typeof raw === 'string') {
    const n = Number(raw)
    if (Number.isFinite(n)) return Math.trunc(n)
  }
  return undefined
}

export const DASHBOARD_SEARCH_DEFAULTS = {
  pipelineRange: PIPELINE_RANGE_KEYS.THIRTY_DAYS,
  activityTab: RECENT_ACTIVITY_SEGMENT_KEYS.all,
  performanceRange: PERFORMANCE_RANGE_KEYS.SEVEN_DAYS,
  targetsRange: MONTHLY_TARGETS_RANGE_KEYS.THIS_MONTH,
} as const
